/**
 * Cache utility for Vue with 5-minute expiration
 * Provides global singleton cache for API responses and computed values
 */

interface CacheEntry<T> {
  value: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<any>>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Get cached value or fetch new value if expired/missing
 * @param key - Unique cache key
 * @param fetchFn - Function to fetch value if not cached
 * @returns Cached or newly fetched value
 */
export function getCachedValue<T>(
  key: string,
  fetchFn: () => T | Promise<T>,
  cacheDuration: number = CACHE_DURATION
): T | Promise<T> {
  const now = Date.now();
  const cached = cache.get(key);

  // Return cached value if it exists and is not expired
  if (cached && now - cached.timestamp < cacheDuration) {
    return cached.value;
  }

  // Fetch new value
  const result = fetchFn();

  // Handle async functions
  if (result instanceof Promise) {
    return result.then((value) => {
      cache.set(key, { value, timestamp: now });
      return value;
    });
  }

  // Handle sync functions
  cache.set(key, { value: result, timestamp: now });
  return result;
}

/**
 * Clear specific cache entry
 * @param key - Cache key to clear
 */
export function clearCache(key: string): void {
  cache.delete(key);
}

/**
 * Clear all cache entries
 */
export function clearAllCache(): void {
  cache.clear();
}

/**
 * Check if a cache entry exists and is valid
 * @param key - Cache key to check
 * @returns true if cache entry exists and is not expired
 */
export function hasCachedValue(key: string): boolean {
  const now = Date.now();
  const cached = cache.get(key);
  return cached !== undefined && now - cached.timestamp < CACHE_DURATION;
}
